import { useFormContext } from 'react-hook-form'
import type { EventTypesPublicGetEventTypeAvailabilities200 } from '@cocoonspace/api'
import { Button } from '@cocoonspace/ui/components/button'
import { FormField } from '@cocoonspace/ui/components/form'
import { Trans, useLingui } from '@lingui/react/macro'
import { format } from 'date-fns/format'
import { enUS as en } from 'date-fns/locale/en-US'
import { fr } from 'date-fns/locale/fr'

import { useAuth } from '~/providers/auth-provider'
import { useAnswerCard } from './provider'
import type { BookingRequestAnswerFormData } from '.'
import type { ProcessedSlot } from './use-available-time-slots'

const HourItem = ({
	value,
	timeSlot,
	onChange,
}: {
	value?: string
	timeSlot: { time: string; spaceId: string }
	onChange: (value: { time: string; spaceId: string }) => void
}) => {
	const isSelected = value === timeSlot.time

	return (
		<Button
			type='button'
			variant={isSelected ? 'default' : 'outline'}
			size='sm'
			className='border-border w-full !py-5 text-left transition-colors'
			onClick={() => onChange(timeSlot)}
		>
			{timeSlot.time}
		</Button>
	)
}

const EmptyContent = () => {
	return (
		<div className='grid h-full items-center p-4 text-center text-sm'>
			<Trans>Aucun créneau disponible</Trans>
		</div>
	)
}

const TimePickerList = ({
	slots,
	day,
}: {
	slots: ProcessedSlot[]
	day: string
}) => {
	const { i18n } = useLingui()
	const form = useFormContext<BookingRequestAnswerFormData>()

	if (!slots.length) return <EmptyContent />

	return (
		<div className='flex flex-col gap-4'>
			<h3 className='mt-3.5 text-center text-lg font-semibold capitalize'>
				{format(new Date(`${day}T00:00:00`), 'EEEE d MMMM', {
					locale: { en, fr }[i18n.locale as 'en' | 'fr'],
				})}
			</h3>

			<FormField
				name='start'
				render={({ field }) => (
					<div className='flex flex-col md:h-[320px] md:overflow-hidden'>
						<div className='grid flex-1 grid-cols-3 place-content-start gap-2 overflow-y-auto'>
							{slots.map((slot) => (
								<HourItem
									key={`${slot.time}-${slot.spaceId}`}
									timeSlot={slot}
									value={field.value}
									onChange={(v) => {
										form.setValue('requestedLocation', {
											type: 'cocoon',
											space_id: v.spaceId,
										})

										return field.onChange(v.time)
									}}
								/>
							))}
						</div>
					</div>
				)}
			/>
		</div>
	)
}

const TimePickerContent = ({
	day,
	slots,
}: {
	day: string
	slots?: ProcessedSlot[]
}) => {
	if (!slots?.length) return <EmptyContent />

	return (
		<TimePickerList
			slots={slots}
			day={day}
		/>
	)
}

const SelectDay = () => {
	const { t } = useLingui()

	return (
		<div className='flex h-full flex-1 items-center justify-center text-center text-sm'>
			{t`Sélectionnez une date`}
		</div>
	)
}

export const TimePicker = ({
	monthSlots,
	embedded = false,
}: {
	monthSlots?: EventTypesPublicGetEventTypeAvailabilities200
	embedded?: boolean
}) => {
	const { t } = useLingui()
	const { isAuth } = useAuth()
	const { setStep } = useAnswerCard()
	const form = useFormContext<BookingRequestAnswerFormData>()
	const day = form.watch('day')
	const slots = day ? monthSlots?.[day] : undefined
	const hasBottomNavigation = isAuth && !embedded

	return (
		<div
			className={`flex min-h-[100px] min-w-0 flex-col gap-4 md:pb-0 ${hasBottomNavigation ? 'pb-40' : 'pb-24'}`}
		>
			{day ? (
				<TimePickerContent
					day={day}
					slots={slots ?? []}
				/>
			) : (
				<SelectDay />
			)}

			<div
				className={`fixed inset-x-0 z-30 border-t bg-white p-4 md:static md:mt-auto md:border-0 md:bg-transparent md:p-0 ${hasBottomNavigation ? 'bottom-[var(--app-navbar-height)]' : 'bottom-0'}`}
			>
				<Button
					type='button'
					className='w-full'
					disabled={
						!day ||
						!form.watch('start') ||
						!form.watch('requestedLocation')?.space_id
					}
					onClick={() => setStep('validation')}
				>
					{t`Continuer`}
				</Button>
			</div>
		</div>
	)
}
